'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { clampQuantity, MAX_QUANTITY, stepDown, stepUp } from '@/lib/quantity-rules';

interface QuantitySelectorProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  /** What − and + move by, and what a typed value is rounded up to: the pack size. */
  step?: number;
  max?: number;
}

/**
 * The quantity stepper in the buy box.
 *
 * The canvas has no quantity control at all — it draws a single full-width buy
 * button — so this is drawn in the redesign's own language rather than copied:
 * the 4px radius and hairline border of the spec grid, and the same `−` / `+`
 * glyphs in the accent that the accordion and the filter sidebar use for their
 * open/close affordance. That is what makes it read as part of this page
 * instead of a leftover from the previous design's 8px pill.
 *
 * `h-12` matches the favourite button beside it, so the row sits on one line.
 *
 * This is the product-page control. The cart has its own
 * (components/cart/quantity-selector.tsx), so restyling here cannot reach it.
 * Both share the arithmetic in lib/quantity-rules.ts.
 */
export function QuantitySelector({
  value,
  onChange,
  min = 1,
  step = 1,
  max = MAX_QUANTITY,
}: QuantitySelectorProps) {
  const rules = { min, step, max };
  const t = useTranslations('common');
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(value));
      return;
    }
    // Rounded UP to a whole pack: typing 1020 of a 50-pack asks for at least 1020.
    const clamped = clampQuantity(parsed, rules);
    onChange(clamped);
    setDraft(String(clamped));
  };

  const stepper =
    'flex h-full w-11 items-center justify-center text-xl font-light leading-none text-accent-700 ' +
    'transition-colors hover:bg-surface-subtle disabled:text-foreground/25 disabled:hover:bg-transparent';

  return (
    // The border carries focus for the whole control: the number input drops
    // its own outline so the field reads as one piece, and without this a
    // keyboard user would land in it with nothing to see.
    <div className="inline-flex h-12 items-center rounded border border-border bg-card transition-colors focus-within:border-accent-600">
      <button
        type="button"
        onClick={() => onChange(stepDown(value, rules))}
        className={`${stepper} rounded-l`}
        disabled={value <= min}
        aria-label={t('decreaseQuantity')}
      >
        <span aria-hidden="true">−</span>
      </button>

      <input
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit();
            e.currentTarget.blur();
          }
        }}
        aria-label={t('quantity')}
        className="h-full w-14 bg-transparent text-center text-[15px] font-semibold tabular-nums text-foreground outline-none"
      />

      <button
        type="button"
        onClick={() => onChange(stepUp(value, rules))}
        className={`${stepper} rounded-r`}
        disabled={value >= max}
        aria-label={t('increaseQuantity')}
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );
}
